"use client";

import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import CustomView from "@/domain/entities/customViews/CustomView";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

type AddViewComboboxProps = {
    setValue: (value: string) => void;
    value: string;
    availableViews: CustomView[];
};

const AddViewCombobox = ({
    setValue,
    value,
    availableViews,
}: AddViewComboboxProps) => {
    const [open, setOpen] = React.useState(false);
    const { t } = useTranslation();

    return (
        <Popover onOpenChange={setOpen} open={open}>
            <PopoverTrigger asChild>
                <Button
                    aria-expanded={open}
                    className="w-[200px]"
                    role="combobox"
                    variant="combobox"
                >
                    <p
                        className={cn(
                            "flex-1 truncate",
                            !value && "text-gray-500",
                        )}
                    >
                        {value ||
                            t(
                                "settings.homepage.customViews.addView.placeholder",
                            )}
                    </p>
                    <ChevronsUpDown className="opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] p-0">
                <Command>
                    <CommandInput
                        className="h-9"
                        placeholder={t(
                            "settings.homepage.customViews.addView.search",
                        )}
                    />
                    <CommandList>
                        <CommandEmpty>
                            {t("settings.homepage.customViews.addView.noViews")}
                        </CommandEmpty>
                        <CommandGroup>
                            {availableViews.map((view) => (
                                <CommandItem
                                    key={view.id}
                                    onSelect={(currentValue) => {
                                        setValue(
                                            currentValue === value
                                                ? ""
                                                : currentValue,
                                        );
                                        setOpen(false);
                                    }}
                                    value={view.name}
                                >
                                    {view.name}
                                    <Check
                                        className={cn(
                                            "ml-auto",
                                            value === view.name
                                                ? "opacity-100"
                                                : "opacity-0",
                                        )}
                                    />
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

export default AddViewCombobox;
